import { DeaConfirmDialogComponent } from '../../dea-pack/dea-confirm-dialog/dea-confirm-dialog.component';
import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { MainLoaderService } from 'src/app/services/main-loader.service';
import { ScholasticYear } from 'src/app/models/scholastic-year.model';
import { ClassesService } from 'src/app/services/classes.service';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ApiError } from 'src/app/models/api/api-error.model';
import { Department } from 'src/app/models/department.model';
import { AuthService } from 'src/app/services/auth.service';
import { Class } from 'src/app/models/class/class.model';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-classes-listing',
  templateUrl: './classes-listing.component.html',
  styleUrls: ['./classes-listing.component.scss'],
})
export class ClassesListingComponent implements OnInit {
  private readonly _baseComponentService = inject(BaseComponentService);
  private readonly _mainLoaderService = inject(MainLoaderService);
  private readonly _classesService = inject(ClassesService);
  private readonly _authService = inject(AuthService);

  protected readonly sectionsConfig$ = this._authService.sectionsState$;

  @Input() page: number = 0;
  @Input() limit: number = 20;
  @Input() department?: string;
  @Input() enableMainBtn: boolean = false;
  @Input() titleMainBtn: string = '';
  @Input() cbMainBtn: Function = () => {};

  protected isError: boolean = false;
  protected loading: boolean = true;
  protected error?: ApiError;
  private text?: string;
  private year: string = ScholasticYear.getCurrent();

  protected classesGroupedBy: [string: Class][] = [];

  get thisYear(): string {
    return this.year;
  }

  ngOnInit(): void {
    this.getData();
  }

  onText(text: string) {
    this.text = text;
    this.getData();
  }

  onPage(page: number) {
    this.page = page;
    this.getData();
  }

  onScholasticYear(year: string) {
    this.year = year;
    this.getData();
  }

  getData() {
    this.loading = true;
    this.isError = false;
    this.error = undefined;
    this._classesService
      .listing({
        year: this.year || undefined,
        department: this.department,
        txt: this.text,
      })
      .subscribe({
        next: (data) => {
          if (data.data != undefined && (data.data as Class[])) {
            const classesArray: Class[] = data.data as Class[];
            let groupBy = classesArray.reduce(function (r, a) {
              if (a.dipartimento) {
                r[a.dipartimento] = r[a.dipartimento] || [];
                r[a.dipartimento].push(a);
              }
              return r;
            }, Object.create(null));
            for (let key in groupBy) {
              const tmpList = groupBy[key];
              groupBy[key] = tmpList.reduce(function (r: any, a: any) {
                if (a.sezione) {
                  r[a.sezione] = r[a.sezione] || [];
                  r[a.sezione].push(a);
                }
                return r;
              }, Object.create(null));
            }
            this.classesGroupedBy = groupBy;
          }
        },
        error: (error) => {
          this.isError = true;
          this.error = new ApiError(error);
          console.error('Classes Listing Error: ', error);
        },
      })
      .add(() => (this.loading = false));
  }

  protected async postNextYearClassesBatch() {
    // Creazione batch delle classi per l'anno successivo
    //
    // Nota:
    // L'API espone solo la creazione della singola classe e la logica di business è attualmente integrata nel relativo endpoint.
    // Poiché un refactoring del backend non è previsto al momento, il batch viene orchestrato dal client
    // riutilizzando l'endpoint esistente (SOLUZIONE NON OTTIMALE)
    //
    // Flusso:
    // 1) Recupero l'elenco delle classi.
    // 2) Rimuovo dalla lista le classi che non prevedono un proseguimento (3° SECONDARIA, 4° ITEM, 5°)
    // 3) Richiamo l'endpoint di creazione per ogni classe (loop)
    // 4) Registro l'esito di ogni operazione.
    // 5) Mostro un riepilogo finale con successi ed eventuali errori.

    const currentSchoolYear: string = this.thisYear;
    const nextSchoolYear: string = `${parseInt(currentSchoolYear.split('-')[0]) + 1}-${parseInt(currentSchoolYear.split('-')[1]) + 1}`;
    // Chiedo conferma dell'operazione
    const confirmDialogRef = this._baseComponentService.dialog.open(
      DeaConfirmDialogComponent,
      {
        maxWidth: '480px',
        minWidth: '450px',
        data: {
          title: 'ATTENZIONE',
          message: `Confermi di voler generare tutte le classi successive in automatico, per l'anno scolastico ${nextSchoolYear}, partendo da quelle dell'anno scolastico ${currentSchoolYear}?`,
          details:
            "Note: l'operazione potrebbe richiedere un po' di tempo, non chiudere questa finestra e non cambiare schermata durante l'operazione",
          falseBtn: 'No, annulla',
          trueBtn: 'Sì, crea classi',
        },
        autoFocus: false,
        restoreFocus: false,
      },
    );
    const dialogResult = await firstValueFrom(confirmDialogRef.afterClosed());
    if (dialogResult !== true) return;
    // Esecuzione:
    this._mainLoaderService.setShow(true);
    try {
      // 1) Recupero l'elenco delle classi.
      const classesResponse = await firstValueFrom(
        this._classesService.listing({ year: currentSchoolYear }),
      );
      // console.info('ALL-CURRENT-CLASSES:', classesResponse.data as Class[]);
      // 2) Rimuovo dalla lista le classi che non prevedono un proseguimento (3° SECONDARIA, 4° ITEM, 5°)
      const classes = (classesResponse.data as Class[]).filter(
        (cl) =>
          cl.anno_scolastico === currentSchoolYear &&
          cl.anno !== 5 &&
          !(cl.dipartimento === Department.ITEM && cl.anno === 4) &&
          !(
            cl.dipartimento === Department.SECONDARIA_DI_I_GRADO &&
            cl.anno === 3
          ),
      );
      // console.info('ALL-CLASSES:', classes);
      // Interrompo il processo se non ci sono classi da generare
      if (classes.length === 0) {
        this._baseComponentService
          .alert(
            'Attenzione',
            'Non sono state trovate classi da generare.',
            'Ok',
          )
          .subscribe();
        return;
      }
      // 3) Richiamo l'endpoint di creazione per ogni classe (loop)
      let results: {
        status: 'SUCCESS' | 'ERROR';
        name: string;
        details?: string;
      }[] = [];
      const sleep = (ms: number) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
      };
      for (let i = 0; i < classes.length; i++) {
        const classe = classes[i];
        this._mainLoaderService.setMessage(
          `Generazione classi ${i + 1}/${classes.length}`,
        );
        const className = `${classe.anno}${classe.sezione} - ${classe.dipartimento}`;
        try {
          const result = await firstValueFrom(
            this._classesService.postNextYearPreview({
              id: classe.id ?? 'ID-ERROR',
              studenti: classe.studenti,
              docenti: classe.docenti,
            }),
          );
          console.info(`${i} | RESULT:`, result);
          results.push({ status: 'SUCCESS', name: className });
        } catch (error: any) {
          console.error('Error:', error);
          results.push({
            status: 'ERROR',
            name: className,
            details: error.message ?? 'Fatal error!',
          });
        }
        // Attendo 1s
        await sleep(1000);
      }
      // 5) Mostro un riepilogo finale con successi ed eventuali errori.
      console.info('RESULTS:', results);
      const successLength = results.filter(
        (r) => r.status === 'SUCCESS',
      ).length;
      const errorResults = results.filter((r) => r.status === 'ERROR');
      this._baseComponentService
        .alert(
          'Riepilogo operazione:',
          `Classi generate: ${successLength} - Errori: ${errorResults.length}${errorResults.length > 0 ? ` (${errorResults.map((cl) => cl.name).join(', ')})` : ''}`,
          'Ok',
        )
        .subscribe();
      this._mainLoaderService.setShow(false);
    } catch (error: any) {
      console.error('Error:', error);
      this._mainLoaderService.setShow(false);
      this._baseComponentService
        .showErrorDialog({
          title: 'Errore',
          message:
            error.message ??
            "Si è verificato un errore fatale, riprova più tardi o contatta l'amministratore",
        })
        .subscribe();
    }
  }
}
